import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Space, Button } from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CameraOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { quotationApi, orderApi } from '@/services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [recentQuotations, setRecentQuotations] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 載入最近報價單
      const quotationsRes = await quotationApi.list({ page: 1, limit: 5 });
      if (quotationsRes.data.success) {
        setRecentQuotations(quotationsRes.data.data);
      }

      // 載入最近訂單
      const ordersRes = await orderApi.list({ page: 1, limit: 5 });
      if (ordersRes.data.success) {
        setRecentOrders(ordersRes.data.data);
      }
    } catch (error) {
      console.error('載入資料失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  // 報價單狀態標籤
  const getQuotationStatusTag = (status) => {
    const statusConfig = {
      draft: { color: 'default', text: '草稿' },
      sent: { color: 'blue', text: '已送出' },
      viewed: { color: 'cyan', text: '已查看' },
      accepted: { color: 'green', text: '已接受' },
      rejected: { color: 'red', text: '已拒絕' },
      expired: { color: 'orange', text: '已過期' },
      converted: { color: 'success', text: '已轉單' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 訂單狀態標籤
  const getOrderStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'default', text: '待確認' },
      confirmed: { color: 'blue', text: '已確認' },
      processing: { color: 'processing', text: '處理中' },
      shipped: { color: 'cyan', text: '已出貨' },
      delivered: { color: 'success', text: '已送達' },
      cancelled: { color: 'error', text: '已取消' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 報價單表格欄位
  const quotationColumns = [
    {
      title: '報價單號',
      dataIndex: 'quotation_no',
      key: 'quotation_no',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/quotations/${record.id}`)}
        >
          {text}
        </Button>
      )
    },
    {
      title: '客戶',
      dataIndex: 'contact_name',
      key: 'contact_name'
    },
    {
      title: '金額',
      dataIndex: 'grand_total',
      key: 'grand_total',
      render: (amount) => `NT$ ${parseFloat(amount).toLocaleString()}`
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: getQuotationStatusTag
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('zh-TW')
    }
  ];

  // 訂單表格欄位
  const orderColumns = [
    {
      title: '訂單號',
      dataIndex: 'order_no',
      key: 'order_no',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/orders/${record.id}`)}
        >
          {text}
        </Button>
      )
    },
    {
      title: '客戶',
      dataIndex: 'contact_name',
      key: 'contact_name'
    },
    {
      title: '金額',
      dataIndex: 'grand_total',
      key: 'grand_total',
      render: (amount) => `NT$ ${parseFloat(amount).toLocaleString()}`
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: getOrderStatusTag
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('zh-TW')
    }
  ];

  return (
    <div>
      {/* 歡迎訊息 */}
      <Card style={{ marginBottom: 24 }}>
        <h2>歡迎回來，{user?.full_name}！</h2>
        <p style={{ color: '#666' }}>
          這是您的報價管理儀表板，可以快速查看最新的報價單與訂單狀態。
        </p>
        <Space>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => navigate('/quotations/create')}
          >
            新增報價單
          </Button>
          <Button
            icon={<CameraOutlined />}
            onClick={() => navigate('/quotations/ocr')}
          >
            OCR 快速建單
          </Button>
        </Space>
      </Card>

      {/* 統計卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本月報價單"
              value={25}
              prefix={<FileTextOutlined />}
              suffix={
                <span style={{ fontSize: 14 }}>
                  <ArrowUpOutlined style={{ color: '#3f8600' }} /> 12%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本月訂單"
              value={18}
              prefix={<ShoppingCartOutlined />}
              suffix={
                <span style={{ fontSize: 14 }}>
                  <ArrowUpOutlined style={{ color: '#3f8600' }} /> 8%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本月營收"
              value={1250000}
              prefix={<DollarOutlined />}
              precision={0}
              suffix={
                <span style={{ fontSize: 14 }}>
                  <ArrowDownOutlined style={{ color: '#cf1322' }} /> 3%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活躍客戶"
              value={156}
              prefix={<UserOutlined />}
              suffix={
                <span style={{ fontSize: 14 }}>
                  <ArrowUpOutlined style={{ color: '#3f8600' }} /> 5%
                </span>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* 最近報價單 */}
      <Card
        title="最近報價單"
        extra={
          <Button type="link" onClick={() => navigate('/quotations')}>
            查看全部
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={quotationColumns}
          dataSource={recentQuotations}
          loading={loading}
          pagination={false}
          rowKey="id"
        />
      </Card>

      {/* 最近訂單 */}
      <Card
        title="最近訂單"
        extra={
          <Button type="link" onClick={() => navigate('/orders')}>
            查看全部
          </Button>
        }
      >
        <Table
          columns={orderColumns}
          dataSource={recentOrders}
          loading={loading}
          pagination={false}
          rowKey="id"
        />
      </Card>
    </div>
  );
};

export default Dashboard;
